import { Button, type ButtonProps } from "@chakra-ui/react";
import { type TezosToolkit } from "@taquito/taquito";
import { type SigningType } from "@tezos-x/octez.connect-wallet";
import {
  type Account,
  type AccountOperations,
  type ChainFullId,
  ChainFullIds,
  type EstimatedAccountOperations,
  type FA12Transfer,
  type FA2Transfer,
  type ImplicitAccount,
  type Network,
  type Operation,
  type SignTransactionOptions,
  type TezTransfer,
  estimate,
  getChainAddress,
  getHash,
  makeAccountOperations,
  totalFee,
  useSignAndSend,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { runSend, runWithdraw } from "@umami/safesend";
import {
  useAsyncActionHandler,
  useGetBestSignerForAccount,
  useGetImplicitAccount,
  useGetOwnedAccount,
  useSelectedNetwork,
} from "@umami/state";
import { type ExecuteParams, type RawPkh } from "@umami/tezos";
import { CustomError } from "@umami/utils";
import { repeat } from "lodash";
import { useState } from "react";
import { useForm, useFormContext } from "react-hook-form";

import { SuccessStep } from "./SuccessStep";

// Convert given optional fields to required
// For example:
// type A = {a?:number, b:string}
// RequiredFields<A, "a"> === {a:number, b:string}
type RequiredFields<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export type FormPageProps<T> = {
  sender?: Account;
  form?: T;
  chainId?: ChainFullId;
  isSafeSendEnabled?: boolean;
};

// FormPagePropsWithSender is the same as FormPageProps but with sender required,
// Use this when we don't want to give the users options to select the sender
// (e.g. the nft and token form)
export type FormPagePropsWithSender<T> = RequiredFields<FormPageProps<T>, "sender">;

// Form values should always have a sender field.
export type BaseFormValues = { sender: RawPkh };

export type SignPageMode = "single" | "batch";

export type SignPageProps<T = undefined> = {
  goBack?: () => void;
  operations: EstimatedAccountOperations;
  mode: SignPageMode;
  data: T;
  chainId?: ChainFullId;
};

export type CalculatedSignProps = {
  fee: number;
  isSigning: boolean;
  onSign: (
    tezosToolkit: TezosToolkit,
    options: SignTransactionOptions,
    chainId?: ChainFullId
  ) => Promise<void>;
  network: any;
};

export type sdkType = "beacon" | "walletconnect";

export type SignRequestId =
  | {
      sdkType: "beacon";
      id: string;
    }
  | {
      sdkType: "walletconnect";
      id: number;
      topic: string;
    };

export type SignHeaderProps = {
  network: Network;
  appName: string;
  appIcon?: string;
  isScam?: boolean;
  validationStatus?: "VALID" | "INVALID" | "UNKNOWN";
  requestId: SignRequestId;
};

export type SdkSignPageProps = {
  operation: EstimatedAccountOperations;
  headerProps: SignHeaderProps;
};

export type SignPayloadProps = {
  requestId: SignRequestId;
  appName: string;
  appIcon?: string;
  payload: string;
  isScam?: boolean;
  validationStatus?: "VALID" | "INVALID" | "UNKNOWN";
  signer: ImplicitAccount;
  signingType: SigningType;
};

export const FormSubmitButton = ({ title = "Preview", ...props }: ButtonProps) => {
  const {
    formState: { isValid },
  } = useFormContext();

  return (
    <Button width="full" isDisabled={!isValid} type="submit" variant="primary" {...props}>
      {title}
    </Button>
  );
};

export const formDefaultValues = <T,>({
  sender,
  form,
  isSafeSendEnabled,
  chainId,
}: FormPageProps<T>) => {
  if (form) {
    return form;
  } else if (sender) {
    return { sender: getChainAddress(sender, chainId).pkh };
  } else if (isSafeSendEnabled) {
    return { isSafeSendEnabled: false };
  } else {
    return {};
  }
};

export const getSender = (operations: EstimatedAccountOperations, chainId: ChainFullId) =>
  operations.sender.chains?.[chainId]?.address ?? operations.sender.address;

// TODO: test this
export const useSignPageHelpers = (
  // the fee & operations you've got from the form
  initialOperations: EstimatedAccountOperations,
  // TODO: remove default value once we have chainId passed from all the form pages
  chainId: ChainFullId = ChainFullIds.TEZOS
) => {
  const [estimationFailed, setEstimationFailed] = useState(false);
  const getSigner = useGetImplicitAccount();
  const [operations, setOperations] = useState<EstimatedAccountOperations>(initialOperations);
  const network = useSelectedNetwork();
  const { isLoading, handleAsyncAction, handleAsyncActionUnsafe } = useAsyncActionHandler();
  const { openWith } = useDynamicModalContext();
  const signAndSend = useSignAndSend();

  const form = useForm<{
    sender: string;
    signer: string;
    executeParams: ExecuteParams[];
    isSafeSendEnabled: boolean;
    safeSendFee: number;
    safeSendFeeLoading: boolean;
    safeSendComments: string;
    chainId: ChainFullId;
  }>({
    mode: "onBlur",
    defaultValues: {
      signer: operations.signer.address.pkh,
      sender: getSender(operations, chainId).pkh,
      executeParams: operations.estimates,
      isSafeSendEnabled: false,
      safeSendFee: 0,
      safeSendFeeLoading: false,
      safeSendComments: "",
      chainId,
    },
  });

  const signer = form.watch("signer");
  const isSafeSendEnabled = form.watch("isSafeSendEnabled");
  // if it fails then the sign button must be disabled
  // and the user is supposed to either come back to the form and amend it
  // or choose another signer
  const reEstimate = async (newSigner: RawPkh) =>
    handleAsyncActionUnsafe(
      async () => {
        const newOperations = await estimate(
          {
            ...operations,
            signer: getSigner(newSigner),
          },
          network
        );
        form.setValue("executeParams", newOperations.estimates);
        setOperations(newOperations);
        setEstimationFailed(false);
      },
      {
        isClosable: true,
        duration: null, // it makes the toast stick until the user closes it
      }
    ).catch(() => setEstimationFailed(true));

  const onSign = async (tezosToolkit: TezosToolkit, options: SignTransactionOptions) =>
    handleAsyncAction(async () => {
      const safeSendComments = form.watch("safeSendComments");

      if (isSafeSendEnabled && operations.operations.length === 1) {
        const operationToSend = operations.operations[0] as
          | TezTransfer
          | FA12Transfer
          | FA2Transfer;

        try {
          const { operation, safeSendHash, secret } = await runSend(
            tezosToolkit,
            network,
            operationToSend,
            safeSendComments === "" ? undefined : safeSendComments
          );

          await openWith(
            <SuccessStep
              chainId={chainId}
              hash={operation.opHash}
              secret={{ safeSendHash, secret }}
            />,
            {
              canBeOverridden: true,
            }
          );

          return operation;
        } catch (error) {
          console.error("SafeSend error:", error);
          throw new CustomError("SafeSend operation failed" + error);
        }
      } else if (operations.operations[0].type === "withdraw_safe_send") {
        const opType = operations.operations[0].assetType;
        const operation = await runWithdraw(
          tezosToolkit,
          network,
          operations.operations[0].secretHash,
          opType
        );
        await openWith(<SuccessStep chainId={chainId} hash={getHash(operation)} />, {
          canBeOverridden: true,
        });

        return operation;
      } else {
        const operation = await signAndSend({
          chainId,
          operations,
          options,
          tezosToolkit,
          estimates: form.watch("executeParams"),
        });

        await openWith(<SuccessStep chainId={chainId} hash={getHash(operation)} />, {
          canBeOverridden: true,
        });

        return operation;
      }
    });

  return {
    fee: totalFee(form.watch("executeParams")),
    isSafeSendEnabled,
    estimationFailed,
    operations,
    isLoading,
    form,
    signer: getSigner(signer),
    reEstimate,
    onSign,
  };
};

export const useMakeFormOperations = <FormValues extends BaseFormValues>(
  toOperation: (formValues: FormValues) => Operation | Operation[]
): ((formValues: FormValues, chainId?: ChainFullId) => AccountOperations) => {
  const getAccount = useGetOwnedAccount();
  const getSigner = useGetBestSignerForAccount();

  return (formValues: FormValues, chainId: ChainFullId = ChainFullIds.TEZOS) => {
    const sender = getAccount(formValues.sender, chainId);

    return makeAccountOperations(sender, getSigner(sender), [toOperation(formValues)].flat());
  };
};

export const getSmallestUnit = (decimals: number): string => {
  if (decimals < 0) {
    console.warn("Decimals cannot be negative");
    decimals = 0;
  }

  const leadingZeroes = decimals === 0 ? "" : "0." + repeat("0", decimals - 1);

  return `${leadingZeroes}1`;
};

export const makeValidateDecimals = (decimals: number) => (val: string) => {
  if (val.includes(".")) {
    const decimalPart = val.split(".")[1];

    if (decimalPart.length > decimals) {
      return `Please enter a value with up to ${decimals} decimal places`;
    }
  }

  return true;
};
